import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import FlowFieldBackground from './landing/FlowFieldBackground';

const EASE_REFINED = [0.22, 1, 0.36, 1];

/* ── Right-column value spec ────────────────────────────────────── */
const SPECS = [
  { metric: '~6 Mo', label: 'Flight Risk Flagged Early' },
  { metric: '89%', label: 'Predictive Accuracy' },
  { metric: 'Live', label: 'Sync With Your HR Stack' },
];

const Arrow = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
    <path d="M2.5 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function Hero({ showFlowBackground = true }) {
  const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: EASE_REFINED, delay },
  });

  return (
    <section
      className="relative overflow-hidden flex items-center"
      style={{
        background: showFlowBackground ? 'var(--bg-base)' : 'transparent',
        minHeight: 'min(90vh, 780px)',
        paddingTop: 132,
        paddingBottom: 72,
      }}
    >
      {showFlowBackground && <FlowFieldBackground uniformStroke />}

      {/* ── Layer 2: horizontal legibility band (fade lines behind copy) ─ */}
      <div aria-hidden="true" className="hero-flow-legibility" />

      {/* ── Content ───────────────────────────────────────────── */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-[1.1fr_minmax(330px,420px)] gap-12 lg:gap-20 items-center">

          {/* Headline */}
          <motion.h1
            className="landing-heading"
            {...fadeUp(0.1)}
            style={{
              margin: 0,
              fontSize: 'clamp(2.7rem, 5.6vw, 5rem)',
              lineHeight: 1.03,
              letterSpacing: '-0.035em',
              color: 'var(--text-primary)',
            }}
          >
            Predict employee turnover.{' '}
            <span
              className="headline-accent hero-headline-accent"
              style={{ display: 'block', marginTop: '0.04em' }}
            >
              Prevent it.
            </span>
          </motion.h1>

          {/* Right rail — frameless editorial spec sheet + CTAs */}
          <motion.div {...fadeUp(0.28)} className="hero-rail">
            <div className="hero-rail-label">
              <span className="hero-rail-dot" />
              Predictive Retention Engine
            </div>

            <div className="hero-spec-sheet">
              {SPECS.map((spec) => (
                <div key={spec.label} className="hero-spec-row">
                  <span className="hero-spec-metric">{spec.metric}</span>
                  <span className="hero-spec-label">{spec.label}</span>
                </div>
              ))}
            </div>

            <div className="hero-rail-cta">
              <Link to="/schedule-demo" className="landing-cta landing-cta--hero">
                <span>Get Free Risk Report</span>
                <Arrow />
              </Link>
              <a
                href="#capabilities"
                className="btn-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth' });
                  window.history.pushState(null, '', '#capabilities');
                }}
              >
                <span>See How It Works</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        .hero-rail { position: relative; }

        .hero-rail-label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 1.4rem;
          font-family: 'SF Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 0.625rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--text-muted);
        }
        .hero-rail-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--hero-accent);
          box-shadow: 0 0 0 0 var(--hero-accent);
          animation: hero-pulse 2.4s ease-in-out infinite;
        }

        .hero-spec-sheet {
          margin: 0 0 1.9rem;
          border-top: 1px solid color-mix(in srgb, var(--text-muted) 24%, transparent);
        }
        .hero-spec-row {
          position: relative;
          display: flex;
          align-items: baseline;
          gap: 0.95rem;
          padding: 0.95rem 0 0.95rem 0.95rem;
          border-bottom: 1px solid color-mix(in srgb, var(--text-muted) 24%, transparent);
          transition: padding-left 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hero-spec-row::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 2px;
          height: 0;
          background: var(--hero-accent);
          border-radius: 2px;
          transition: height 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hero-spec-row:hover::before { height: 60%; }
        .hero-spec-row:hover { padding-left: 1.25rem; }

        .hero-spec-metric {
          flex-shrink: 0;
          min-width: 4.4rem;
          font-family: var(--font-landing-display);
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1;
          letter-spacing: -0.02em;
          color: var(--hero-headline-accent);
          font-feature-settings: 'tnum' 1;
        }
        .hero-spec-label {
          font-family: 'DM Sans', Inter, sans-serif;
          font-size: 0.9375rem;
          line-height: 1.4;
          color: var(--text-secondary);
        }

        .hero-rail-cta {
          display: flex;
          flex-wrap: wrap;
          gap: 0.625rem;
        }

        @keyframes hero-pulse {
          0%, 100% { box-shadow: 0 0 0 0 var(--hero-accent); opacity: 1; }
          50% { box-shadow: 0 0 0 4px transparent; opacity: 0.55; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-rail-dot { animation: none !important; }
          .hero-spec-row, .hero-spec-row::before { transition: none !important; }
        }
      `}</style>
    </section>
  );
}
